import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Activity, Heart, ArrowLeft, RefreshCw, Eye,
  Mail, Calendar, Clock, AlertTriangle, CheckCircle2, XCircle, UserCog,
  TrendingUp, Loader2, Search, PoundSterling, Sparkles, Download, CalendarClock,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";
import GroupsAdminCard from "@/components/GroupsAdminCard";
import DemoDayDialog from "@/components/DemoDayDialog";
import SendPitchDeckCard from "@/components/SendPitchDeckCard";

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const ago = (Date.now() - new Date(iso).getTime()) / 1000;
    if (ago < 60) return `${Math.floor(ago)}s ago`;
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    return `${Math.floor(ago / 86400)}d ago`;
  } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Friendly labels for audit actions
const ACTION_LABELS = {
  "auth.login.ok": { icon: CheckCircle2, colour: "text-success", label: "Logged in" },
  "auth.login.fail": { icon: XCircle, colour: "text-destructive", label: "Failed login" },
  "auth.register": { icon: UserCog, colour: "text-primary", label: "Signed up" },
  "public.quote.viewed": { icon: Eye, colour: "text-primary", label: "Quote viewed" },
  "public.quote.responded": { icon: CheckCircle2, colour: "text-success", label: "Quote actioned" },
  "public.callback.requested": { icon: AlertTriangle, colour: "text-warning", label: "Callback requested" },
  "admin.impersonate.start": { icon: ShieldCheck, colour: "text-warning", label: "Started impersonation" },
};

export default function AdminPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showAllUsage, setShowAllUsage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  // Demo tenants drown out real prospects in the commissions table by 50:1.
  // Default to OFF — toggle is right above the table.
  const [showDemos, setShowDemos] = useState(false);

  const loadCommissions = useCallback(async (withDemos) => {
    try {
      const r = await api.get("/billing/commissions", { params: { include_demos: withDemos } });
      setCommissions(r.data);
    } catch { /* leave as-is */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [meR, usersR, eventsR, healthR, commR, billR, usageR] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/users"),
        api.get("/admin/activity?limit=80"),
        api.get("/admin/health"),
        api.get("/billing/commissions", { params: { include_demos: showDemos } }).catch(() => ({ data: null })),
        api.get("/admin/billing-settings-status").catch(() => ({ data: null })),
        api.get("/admin/usage").catch(() => ({ data: null })),
      ]);
      setMe(meR.data);
      setUsers(usersR.data.users || []);
      setEvents(eventsR.data.events || []);
      setHealth(healthR.data);
      setCommissions(commR.data);
      setBillingStatus(billR.data);
      setUsage(usageR.data);
      setError("");
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("Platform owner access only.");
      } else {
        setError(e?.response?.data?.detail || "Couldn't load admin data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showDemos]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh activity feed every 15 seconds
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/admin/activity?limit=80").then(r => setEvents(r.data.events || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const impersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.email}?\n\nYou will see exactly what they see for the next 8 hours. The action is recorded in the audit log.`)) return;
    try {
      const r = await api.post("/admin/impersonate", { user_id: u.id, reason: "remote support" });
      localStorage.setItem("token", r.data.token);
      // Stash an impersonation banner flag for the dashboard
      sessionStorage.setItem("impersonate_banner", JSON.stringify({
        as_email: r.data.as_user.email, as_name: r.data.as_user.name,
      }));
      toast.success(`Now signed in as ${r.data.as_user.email}`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't impersonate");
    }
  };

  const [demoDayTarget, setDemoDayTarget] = useState(null);
  const loadDemoDay = (u) => setDemoDayTarget(u);

  // Steve is the sole platform owner of ServiceSell PRO. Every other
  // account (RRG managers, Toyota advisors, ENSO, Hyundai) is a workshop
  // *user* — not an owner. Only surface the OWNER badge for Steve; show
  // everyone else's role_in_workshop as a subtle grey label.
  // Legacy signups defaulted `role_in_workshop = "owner"` per workshop
  // so we treat that value as "manager" for badge purposes only — the
  // permission model in the workshop stays intact (see server.py).
  const [hideTestAccounts, setHideTestAccounts] = useState(true);
  const TEST_ACCOUNT_PATTERNS = [
    /@example\.com$/i,
    /@autoshop\.com$/i,       // early seed accounts
    /^qa[._]/i,
    /^pwd[-_]/i,
    /^aivhc[-_]/i,
    /^audit\.test\./i,
    /^phase-b-test-/i,
    /^owner-[0-9a-f]{6}@/i,
    /^advisor-[0-9a-f]{6}@/i,
    /^foo@bar\./i,
    /^test\.tech\./i,
    /^smtp-test@/i,
    /^demo[-.]/i,              // demo-XXXXX, demo.foo
    /-demo-/i,                 // rrg-demo-XXX, toyota-demo-XXX, hyundai-demo-XXX
    /\.demo\./i,               // howto.demo.test1
  ];
  const isTestAccount = (u) => {
    const e = (u.email || "").toLowerCase();
    return TEST_ACCOUNT_PATTERNS.some((rx) => rx.test(e));
  };

  const filtered = users.filter((u) => {
    if (hideTestAccounts && !u.platform_owner && isTestAccount(u)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) ||
           (u.name || "").toLowerCase().includes(q) ||
           (u.workshop_name || "").toLowerCase().includes(q);
  });

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white p-6">
        <div className="text-center max-w-md">
          <ShieldCheck className="w-12 h-12 text-destructive mx-auto" />
          <h1 className="font-display text-2xl font-black mt-4">Access denied</h1>
          <p className="text-white/60 mt-2 text-sm">{error}</p>
          <Link to="/dashboard" className="btn-primary text-sm mt-6 inline-flex">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-page">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur sticky top-0 z-30 bg-black/70">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLink />
            <span className="px-2 py-0.5 rounded-full bg-warning/15 border border-warning/40 text-warning text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Internal accounts
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setRefreshing(true); load(); }} className="btn-outline text-xs" data-testid="admin-refresh-btn">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
            <Link to="/dashboard" className="btn-outline text-xs"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-4 sm:px-6 py-8 space-y-8">
        {loading ? (
          <div className="text-center py-16 text-white/50">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Loading platform data…
          </div>
        ) : (
          <>
            {/* System health */}
            <section data-testid="admin-health-section">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-4 h-4 text-success" />
                <h2 className="font-display font-bold text-lg">System health</h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <StatTile label="Workshops" value={health?.totals?.workshops || 0} icon={ShieldCheck} colour="primary" testid="admin-stat-workshops" />
                <StatTile label="Users" value={health?.totals?.users || 0} icon={Users} colour="primary" testid="admin-stat-users" />
                <StatTile label="Total jobs" value={health?.totals?.jobs || 0} icon={Activity} colour="primary" testid="admin-stat-jobs" />
                <StatTile label="Jobs · last 24h" value={health?.last_24h?.jobs_created || 0} icon={TrendingUp} colour="success" testid="admin-stat-jobs-24h" />
                <StatTile label="Audit events" value={health?.totals?.audit_events || 0} icon={Eye} colour="warning" testid="admin-stat-audit" />
              </div>
              <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-integrations">
                <IntegrationPill name="IONOS Email" ok={!!health?.integrations?.smtp} />
                <IntegrationPill name="Twilio SMS" ok={!!health?.integrations?.twilio} dormant={!health?.integrations?.twilio} dormantNote="Awaiting UK bundle" />
                <IntegrationPill name="DVSA MOT" ok={!!health?.integrations?.dvsa} />
                <IntegrationPill name="AI (Emergent LLM)" ok={!!health?.integrations?.emergent_llm} />
              </div>
            </section>

            {/* Bank-details safety banner — refuses to let you email an
                invoice with placeholder bank details. Only shows when missing. */}
            {billingStatus && !billingStatus.ok && (
              <section
                className="rounded-lg border border-warning/50 bg-warning/[0.08] p-4 flex items-start gap-3"
                data-testid="bank-details-warning-banner"
              >
                <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-warning text-sm">
                    Set your bank details before sending live invoices
                  </div>
                  <p className="text-xs text-white/70 mt-1 leading-relaxed">
                    The invoice PDF currently prints placeholder text where your
                    BACS details should be. Set these environment variables on
                    the backend, then redeploy:
                  </p>
                  <ul className="text-[11px] font-mono text-white/60 mt-2 space-y-0.5">
                    {(billingStatus.missing || []).map((k) => (
                      <li key={k}>· {k}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-white/50 mt-2">
                    Until then, the <span className="font-semibold text-white/80">Send invoice</span> button below is disabled to stop accidental
                    dispatch of an invoice with "TBC" in the bank fields.
                  </p>
                </div>
              </section>
            )}

            {/* Commissions — 3% of customer-authorised extras per workshop */}
            <CommissionsCard
              data={commissions}
              showDemos={showDemos}
              onToggleDemos={() => setShowDemos((v) => !v)}
              bankDetailsReady={billingStatus?.ok !== false}
            />

            {/* Workshop feature flags + commission-rate overrides */}
            <WorkshopFeaturesCard onChanged={load} />

            {/* Dealer Groups (parent entities over multiple workshops) */}
            <GroupsAdminCard />
            <DemoDayDialog target={demoDayTarget} onClose={() => setDemoDayTarget(null)} onDone={load} />

            {/* Send a personalised pitch deck (outbound sales workflow) */}
            <SendPitchDeckCard />

            {/* AI VHC video calibration tester (platform-owner only, hidden from dealers) */}
            <Link
              to="/accounts/ai-vhc-tester"
              className="block rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-primary/40 transition-colors p-5 group"
              data-testid="ai-vhc-tester-link"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-primary group-hover:rotate-12 transition-transform" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-display font-bold text-lg">AI VHC video tester</h2>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-warning border border-warning/40 rounded px-1.5 py-0.5">
                      INTERNAL · CALIBRATION
                    </span>
                  </div>
                  <p className="text-xs text-white/50 mt-0.5">
                    Drag-drop a real workshop walkaround → see the structured findings the pipeline would extract. No DB writes, no dealership sees a thing.
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-white/40 rotate-180 group-hover:text-primary transition-colors" />
              </div>
            </Link>

            {/* Usage & costs */}
            <section data-testid="admin-usage-section">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-primary" />
                <h2 className="font-display font-bold text-lg">Usage &amp; costs</h2>
                {usage && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 ml-2">
                    total SMS spend £{Number(usage.total_sms_cost_gbp || 0).toFixed(2)}
                  </span>
                )}
              </div>
              {!usage ? (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/50">Loading usage…</div>
              ) : (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-usage-table">
                    <thead>
                      <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-white/40 border-b border-white/10">
                        <th className="px-4 py-2.5">Workshop / tenant</th>
                        <th className="px-4 py-2.5">Logins</th>
                        <th className="px-4 py-2.5 text-right">Jobs</th>
                        <th className="px-4 py-2.5 text-right">SMS sent</th>
                        <th className="px-4 py-2.5 text-right">SMS cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {usage.rows.filter((r) => showAllUsage || r.sms_count > 0).map((r) => (
                        <tr key={r.workshop_id || "none"} className="hover:bg-white/[0.03]" data-testid={`usage-row-${r.workshop_id}`}>
                          <td className="px-4 py-2.5 font-semibold">{r.workshop_name}</td>
                          <td className="px-4 py-2.5 text-xs text-white/60">
                            {(r.users || []).map((u) => u.name || u.email).join(", ") || "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono">{r.jobs_count}</td>
                          <td className="px-4 py-2.5 text-right font-mono">
                            {r.sms_count}{r.sms_failed > 0 && <span className="text-destructive text-[10px]"> ({r.sms_failed} failed)</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-bold text-primary">£{Number(r.sms_cost_gbp).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-2.5 text-[11px] text-white/40 border-t border-white/10">
                    SMS costs are exact (logged per send from Twilio segments). Emails are included in your IONOS plan.
                    AI usage bills to your Emergent Universal Key at platform level — see Profile → Usage in Emergent for the balance.
                  </div>
                </div>
              )}
            </section>

            {/* Activity feed */}
            <section data-testid="admin-activity-section">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-primary" />
                <h2 className="font-display font-bold text-lg">Live activity</h2>
                <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 ml-2">auto-refresh · 15s</span>
              </div>
              {events.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/50">
                  No activity yet. Events will appear here as people sign up, log in, view quotes, and authorise work.
                </div>
              ) : (
                <ul className="rounded-lg border border-white/10 bg-white/[0.02] divide-y divide-white/5 max-h-[420px] overflow-y-auto" data-testid="admin-events-list">
                  {events.map((e) => {
                    const meta = ACTION_LABELS[e.action] || { icon: Activity, colour: "text-white/60", label: e.action };
                    const I = meta.icon;
                    return (
                      <li key={e.id} className="px-4 py-2.5 flex items-start gap-3 text-sm hover:bg-white/[0.03] transition-colors" data-testid={`admin-event-${e.id}`}>
                        <I className={`w-4 h-4 mt-0.5 shrink-0 ${meta.colour}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-semibold">{meta.label}</span>
                            {e.user_email && <span className="text-xs text-white/60">· {e.user_email}</span>}
                            {e.details?.email && !e.user_email && <span className="text-xs text-white/60">· {e.details.email}</span>}
                          </div>
                          {e.details && Object.keys(e.details).length > 0 && (
                            <div className="text-[11px] text-white/50 mt-0.5 truncate">
                              {Object.entries(e.details)
                                .filter(([k]) => !["email", "ip"].includes(k))
                                .slice(0, 4)
                                .map(([k, v]) => `${k}: ${String(v).slice(0, 30)}`)
                                .join(" · ")}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-white/40 shrink-0 whitespace-nowrap">{fmtRelative(e.at)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Users / workshops */}
            <section data-testid="admin-users-section">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <h2 className="font-display font-bold text-lg">Workshops &amp; users</h2>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 ml-2">{filtered.length} of {users.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/60 cursor-pointer select-none"
                    data-testid="admin-hide-tests-label"
                  >
                    <input
                      type="checkbox"
                      checked={hideTestAccounts}
                      onChange={(e) => setHideTestAccounts(e.target.checked)}
                      className="accent-primary"
                      data-testid="admin-hide-tests-toggle"
                    />
                    Hide test accounts
                  </label>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Filter by email / name / workshop"
                      className="bg-white/[0.04] border border-white/15 rounded-md pl-8 pr-3 py-1.5 text-sm w-72 focus:outline-none focus:border-primary/50"
                      data-testid="admin-users-search"
                    />
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                    <tr>
                      <th className="text-left px-4 py-2">Workshop / user</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-right px-3 py-2">Jobs</th>
                      <th className="text-right px-3 py-2">Approved</th>
                      <th className="text-right px-3 py-2">Last login</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filtered.map((u) => (
                      <tr key={u.id} className="hover:bg-white/[0.03]" data-testid={`admin-user-${u.id}`}>
                        <td className="px-4 py-2">
                          <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                            {u.workshop_name || u.name || "—"}
                            {u.platform_owner && (
                              <span
                                className="px-1.5 py-0.5 rounded-full bg-warning/15 border border-warning/40 text-warning text-[9px] font-bold uppercase tracking-wider whitespace-nowrap"
                                title="Sole owner of ServiceSell PRO"
                              >
                                Platform owner
                              </span>
                            )}
                            {!u.platform_owner && u.role_in_workshop && (
                              <span
                                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-white/[0.06] border border-white/15 text-white/60 whitespace-nowrap"
                                title="Their role inside their own workshop — not a ServiceSell PRO role"
                              >
                                {u.role_in_workshop === "owner" ? "Manager" : u.role_in_workshop}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-white/40">{u.name}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-white/70 break-all max-w-[200px]">{u.email}</td>
                        <td className="px-3 py-2 text-right font-mono text-white/80">{u.jobs_total}</td>
                        <td className="px-3 py-2 text-right font-mono text-success">{u.jobs_approved}</td>
                        <td className="px-3 py-2 text-right text-[11px] text-white/60 whitespace-nowrap" title={u.last_login_at || ""}>{fmtRelative(u.last_login_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1.5">
                            {!u.platform_owner && (
                            <button
                              onClick={() => impersonate(u)}
                              className="px-2 py-1 rounded-md border border-white/15 hover:border-primary/40 hover:bg-primary/10 text-[11px] font-bold transition-colors inline-flex items-center gap-1"
                              data-testid={`admin-impersonate-${u.id}`}
                              title="View the app as this user (audited)"
                            >
                              <UserCog className="w-3 h-3" /> View as
                            </button>
                            )}
                            {u.workshop_id && (
                              <button
                                onClick={() => loadDemoDay(u)}
                                className="px-2 py-1 rounded-md border border-warning/30 text-warning hover:bg-warning/10 text-[11px] font-bold transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid={`admin-demo-day-${u.id}`}
                                title="Wipe this workshop's jobs and load a Toyota demo day (safe dummy contacts — optionally put your own mobile on one car)"
                              >
                                <CalendarClock className="w-3 h-3" /> Demo day
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, colour = "primary", testid }) {
  const accent = colour === "success" ? "text-success border-success/30 bg-success/[0.04]"
              : colour === "warning" ? "text-warning border-warning/30 bg-warning/[0.04]"
              : colour === "destructive" ? "text-destructive border-destructive/30 bg-destructive/[0.04]"
              : "text-primary border-primary/30 bg-primary/[0.04]";
  return (
    <div className={`rounded-lg border ${accent} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value}</div>
    </div>
  );
}

function IntegrationPill({ name, ok, dormant, dormantNote }) {
  return (
    <div className={`rounded-lg border p-3 text-xs flex items-center gap-2 ${ok ? "border-success/30 bg-success/[0.04]" : dormant ? "border-warning/30 bg-warning/[0.04]" : "border-destructive/30 bg-destructive/[0.04]"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-success animate-pulse" : dormant ? "bg-warning" : "bg-destructive"}`} />
      <div className="flex-1">
        <div className="font-semibold text-white text-sm">{name}</div>
        <div className={`text-[10px] uppercase tracking-wider ${ok ? "text-success" : dormant ? "text-warning" : "text-destructive"}`}>
          {ok ? "Live" : dormant ? (dormantNote || "Dormant") : "Not configured"}
        </div>
      </div>
    </div>
  );
}

function gbp(n) { return `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtMonthRange(period) {
  if (!period?.from) return "";
  try {
    return new Date(period.from).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  } catch { return period.from; }
}

/**
 * Commissions card — 3% of customer-authorised extras for the current calendar
 * month, by tenant. Renders nothing for non-platform-owner accounts (the
 * endpoint silently falls back to their own data, but the platform-owner
 * cross-tenant view is the only audience we design for here).
 */
function CommissionsCard({ data, showDemos = false, onToggleDemos, bankDetailsReady = true }) {
  if (!data) {
    return (
      <section data-testid="admin-commissions-empty">
        <div className="flex items-center gap-2 mb-3">
          <PoundSterling className="w-4 h-4 text-success" />
          <h2 className="font-display font-bold text-lg">Commissions · this month</h2>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/50">
          No commission data available yet.
        </div>
      </section>
    );
  }
  const t = data.totals || {};
  return (
    <section data-testid="admin-commissions-section">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PoundSterling className="w-4 h-4 text-success" />
          <h2 className="font-display font-bold text-lg">Commissions · {fmtMonthRange(data.period)}</h2>
        </div>
        <div className="flex items-center gap-3">
          {onToggleDemos && (
            <button
              type="button"
              onClick={onToggleDemos}
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border transition-colors
                ${showDemos
                  ? "border-warning/50 text-warning bg-warning/10 hover:bg-warning/15"
                  : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                }`}
              data-testid="commissions-toggle-demos"
              title={showDemos
                ? "Hide demo tenants from this table"
                : "Show demo tenants (currently hidden — 100+ rows)"}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${showDemos ? "bg-warning" : "bg-white/30"}`} />
              {showDemos ? "Demos visible" : "Demos hidden"}
            </button>
          )}
          <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">
            3% of customer-authorised extras
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatTile label="Approved extras" value={gbp(t.approved_total)} icon={TrendingUp} colour="primary" testid="comm-stat-approved" />
        <StatTile label="Platform fees + commissions" value={gbp(t.commission_due)} icon={PoundSterling} colour="success" testid="comm-stat-due" />
        <StatTile label={`Service plans · £${(t.service_plan_commission_due || 0).toFixed(2)}`} value={t.service_plans_sold || 0} icon={Sparkles} colour="success" testid="comm-stat-plans" />
        <StatTile label="Grand total due" value={gbp(t.grand_total_due ?? ((t.commission_due || 0) + (t.service_plan_commission_due || 0)))} icon={PoundSterling} colour="warning" testid="comm-stat-grand-total" />
      </div>
      {(t.excluded_items || 0) > 0 && (
        <div
          className="rounded-md border border-white/15 bg-white/[0.03] px-4 py-2.5 mb-4 text-xs text-white/70 flex items-start gap-2"
          data-testid="comm-excluded-banner"
          title="Items the advisor closed personally on the phone after a 'Speak to advisor' callback. The app didn't sell the work, so no commission is charged on it."
        >
          <span className="font-mono text-warning text-[11px] tracking-wider uppercase shrink-0 mt-0.5">Phone sales excluded</span>
          <span>
            <span className="font-mono-plex font-bold text-white">{gbp(t.excluded_total)}</span> across {t.excluded_items} item{t.excluded_items === 1 ? "" : "s"} the advisor closed personally after a callback —{" "}
            <span className="text-white/50">not charged 3%</span>.
          </span>
        </div>
      )}
      {(data.by_workshop || []).length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5 text-center text-sm text-white/50">
          No customer-authorised work this month. Once a dealer's customer approves extras via a share link, it'll appear here.
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm" data-testid="commissions-table">
            <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Dealership</th>
                <th className="text-right px-4 py-2 font-bold">Rate</th>
                <th className="text-right px-4 py-2 font-bold">Approved</th>
                <th className="text-right px-4 py-2 font-bold">Jobs</th>
                <th className="text-right px-4 py-2 font-bold">Plans sold</th>
                <th className="text-right px-4 py-2 font-bold">Total due</th>
                <th className="text-right px-4 py-2 font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.by_workshop.map((w) => (
                <tr key={w.workshop_id} className="hover:bg-white/[0.03]" data-testid={`comm-row-${w.workshop_id}`}>
                  <td className="px-4 py-2.5 font-semibold text-white">{w.workshop_name}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-white/70">{(w.rate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {gbp(w.approved_total)}
                    {(w.excluded_items || 0) > 0 && (
                      <div
                        className="text-[10px] text-white/40 font-mono mt-0.5"
                        title={`${w.excluded_items} item(s) sold by advisor on the phone — excluded from commission`}
                        data-testid={`comm-row-excluded-${w.workshop_id}`}
                      >
                        ({gbp(w.excluded_total)} phone)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-white/60">{w.job_count}</td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {w.service_plans_sold ? (
                      <span className="text-success">{w.service_plans_sold} · £{(w.service_plan_commission || 0).toFixed(2)}</span>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-success">
                    {gbp((w.commission_due || 0) + (w.service_plan_commission || 0))}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                      <DownloadStatementButton workshop={w} period={data.period} />
                      <SendInvoiceButton workshop={w} period={data.period} disabled={!bankDetailsReady} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}



/**
 * Workshop feature flags + per-tenant commission-rate override panel.
 * Platform-owner only. Calls existing admin endpoints:
 *   GET  /api/admin/users    (returns the workshops list)
 *   POST /api/admin/workshops/{id}/features    (toggle paid add-on flags)
 *   PATCH /api/admin/workshops/{id}/commission-rate
 */
function WorkshopFeaturesCard({ onChanged }) {
  const [workshops, setWorkshops] = useState([]);
  const [savingRow, setSavingRow] = useState(null); // workshop_id currently saving

  const fetchWorkshops = useCallback(async () => {
    try {
      const r = await api.get("/admin/users");
      setWorkshops(r.data.workshops || []);
    } catch (e) {
      toast.error("Couldn't load workshops");
    }
  }, []);

  useEffect(() => { fetchWorkshops(); }, [fetchWorkshops]);

  const toggleFeature = async (wid, feature, current) => {
    setSavingRow(wid);
    try {
      await api.post(`/admin/workshops/${wid}/features`, { feature, enabled: !current });
      toast.success(`${feature.replace("_", " ")} ${!current ? "enabled" : "disabled"}`);
      await fetchWorkshops();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingRow(null); }
  };

  const setRate = async (wid, ratePercent) => {
    const rate = Number(ratePercent) / 100;
    if (Number.isNaN(rate) || rate < 0 || rate > 0.10) {
      toast.error("Rate must be between 0% and 10%");
      return;
    }
    setSavingRow(wid);
    try {
      await api.patch(`/admin/workshops/${wid}/commission-rate?rate=${rate}`);
      toast.success(`Commission rate set to ${ratePercent}%`);
      await fetchWorkshops();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingRow(null); }
  };

  return (
    <section data-testid="admin-workshop-features-section">
      <div className="flex items-center gap-2 mb-3">
        <UserCog className="w-4 h-4 text-primary" />
        <h2 className="font-display font-bold text-lg">Workshop add-ons &amp; commission rates</h2>
        <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 ml-2">{workshops.length} tenant{workshops.length === 1 ? "" : "s"}</span>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Workshop</th>
              <th className="text-center px-3 py-2 font-bold">Condition Photos<br/><span className="text-[9px] opacity-60">£75/mo</span></th>
              <th className="text-center px-3 py-2 font-bold">Service Plans<br/><span className="text-[9px] opacity-60">£5/plan</span></th>
              <th className="text-center px-3 py-2 font-bold">Video AI VHC<br/><span className="text-[9px] opacity-60">preview</span></th>
              <th className="text-right px-4 py-2 font-bold">Commission %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {workshops.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-white/40 text-sm">No workshops yet.</td></tr>
            )}
            {workshops.map((w) => (
              <WorkshopFeatureRow
                key={w.id}
                w={w}
                saving={savingRow === w.id}
                onToggle={(feat, cur) => toggleFeature(w.id, feat, cur)}
                onRate={(pct) => setRate(w.id, pct)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function WorkshopFeatureRow({ w, saving, onToggle, onRate }) {
  const features = w.features || {};
  const [rateDraft, setRateDraft] = useState(((w.commission_rate ?? 0.03) * 100).toFixed(1));
  const persisted = ((w.commission_rate ?? 0.03) * 100).toFixed(1);
  const dirty = rateDraft !== persisted;
  return (
    <tr className="hover:bg-white/[0.03]" data-testid={`feature-row-${w.id}`}>
      <td className="px-4 py-2.5">
        <div className="font-semibold text-white">{w.name}</div>
        <div className="text-[10px] font-mono text-white/40">{w.id.slice(0, 12)}…</div>
      </td>
      {["condition_photos", "service_plans", "video_ai_vhc"].map((feat) => {
        const on = !!features[feat];
        return (
          <td key={feat} className="px-3 py-2.5 text-center">
            <button
              type="button"
              disabled={saving}
              onClick={() => onToggle(feat, on)}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                on ? "bg-success" : "bg-white/15"
              } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid={`toggle-${feat}-${w.id}`}
              title={on ? "Click to disable" : "Click to enable"}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${on ? "translate-x-5" : ""}`} />
            </button>
          </td>
        );
      })}
      <td className="px-4 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={rateDraft}
            onChange={(e) => setRateDraft(e.target.value)}
            className="w-16 bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-right text-sm font-mono"
            data-testid={`rate-input-${w.id}`}
          />
          <span className="text-white/40 text-xs">%</span>
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => onRate(rateDraft)}
            className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
              dirty && !saving ? "bg-primary/20 text-primary hover:bg-primary/30" : "bg-white/5 text-white/30 cursor-not-allowed"
            }`}
            data-testid={`rate-save-${w.id}`}
          >
            {saving ? "…" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Per-row "Download statement" — itemised PDF audit doc for the workshop.
 * Lists every billable + excluded line item in the period. Doesn't email
 * anything; just downloads to the platform owner's machine.
 */
function DownloadStatementButton({ workshop, period }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ workshop_id: workshop.workshop_id });
      if (period?.from) params.set("from", period.from);
      if (period?.to) params.set("to", period.to);
      const r = await api.get(`/billing/statement?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement-${workshop.workshop_name?.replace(/\s+/g, "-") || workshop.workshop_id}-${period?.from?.slice(0, 7) || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download statement");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      disabled={busy}
      onClick={download}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/15 bg-white/[0.04] text-white/80 text-[11px] font-bold hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50"
      title="Download itemised PDF statement"
      data-testid={`download-statement-btn-${workshop.workshop_id}`}
    >
      <Download className="w-3 h-3" /> {busy ? "…" : "Statement"}
    </button>
  );
}


/**
 * Per-row "Send invoice" action. Uses the platform-owner-only
 * /admin/workshops/{id}/send-invoice endpoint to email the dealership a
 * PDF invoice for the current commissions period.
 */
function SendInvoiceButton({ workshop, period, disabled: hardDisabled = false }) {
  const [busy, setBusy] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [confirming, setConfirming] = useState(false);

  const total = (workshop.commission_due || 0) + (workshop.service_plan_commission || 0);
  const zero = total <= 0;
  const blocked = zero || hardDisabled;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        recipient_email: emailDraft.trim() || null,
        date_from: period?.from,
        date_to: period?.to,
        force: zero,
      };
      const r = await api.post(`/admin/workshops/${workshop.workshop_id}/send-invoice`, payload);
      toast.success(`Invoice ${r.data.invoice_ref} sent to ${r.data.sent_to} · £${r.data.amount.toFixed(2)}`);
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invoice");
    } finally { setBusy(false); }
  };

  if (!confirming) {
    return (
      <button
        type="button"
        disabled={blocked}
        onClick={() => setConfirming(true)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-bold transition-colors ${
          blocked
            ? "border-white/10 text-white/30 cursor-not-allowed"
            : "border-success/30 bg-success/[0.06] text-success hover:bg-success/[0.15]"
        }`}
        title={
          hardDisabled
            ? "Set your bank details first (see banner above)"
            : zero
              ? "Nothing to invoice"
              : "Send PDF invoice via IONOS"
        }
        data-testid={`send-invoice-btn-${workshop.workshop_id}`}
      >
        <Mail className="w-3 h-3" /> Send invoice
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`send-invoice-confirm-${workshop.workshop_id}`}>
      <input
        type="email"
        autoFocus
        placeholder="dealer billing email"
        value={emailDraft}
        onChange={(e) => setEmailDraft(e.target.value)}
        className="px-2 py-1 rounded border border-white/15 bg-white/[0.04] text-white text-[11px] font-mono w-44"
        data-testid={`send-invoice-email-${workshop.workshop_id}`}
      />
      <button type="button" disabled={busy} onClick={submit}
        className="px-2 py-1 rounded bg-success text-success-foreground text-[10px] font-bold disabled:opacity-50"
        data-testid={`send-invoice-confirm-btn-${workshop.workshop_id}`}>
        {busy ? "…" : "Send"}
      </button>
      <button type="button" disabled={busy} onClick={() => setConfirming(false)}
        className="px-1.5 py-1 rounded text-white/40 hover:text-white text-[10px]"
        data-testid={`send-invoice-cancel-${workshop.workshop_id}`}>
        ×
      </button>
    </div>
  );
}

